import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
    MessageSquare, 
    ExternalLink, 
    CheckCircle, 
    AlertCircle, 
    RefreshCw,
    Send,
    Hash,
    Shield,
    Unlink,
    User,
    Users,
    Info
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { router } from "@inertiajs/react";

export function UserSlackConnection({ user }) {
    const { toast } = useToast();
    const [connectionStatus, setConnectionStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [channels, setChannels] = useState([]);
    const [isLoadingChannels, setIsLoadingChannels] = useState(false);
    const [showTestModal, setShowTestModal] = useState(false);
    const [testChannel, setTestChannel] = useState("");
    const [testMessage, setTestMessage] = useState("");
    const [isSendingTest, setIsSendingTest] = useState(false);

    // Load connection status on mount
    useEffect(() => {
        loadConnectionStatus();
    }, []);

    const loadConnectionStatus = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/slack/status');
            const data = await response.json();
            setConnectionStatus(data);
            
            if (data.connected) {
                loadChannels();
            }
        } catch (error) {
            console.error('Failed to load Slack status:', error);
            toast({
                title: "Error",
                description: "Failed to check Slack connection status",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const loadChannels = async () => {
        setIsLoadingChannels(true);
        try {
            const response = await fetch('/slack/channels');
            const data = await response.json();
            
            if (response.ok) {
                setChannels(data.channels || []);
            } else {
                throw new Error(data.error || 'Failed to load channels');
            }
        } catch (error) {
            console.error('Failed to load channels:', error);
            toast({
                title: "Error", 
                description: "Failed to load Slack channels: " + error.message,
                variant: "destructive",
            });
        } finally {
            setIsLoadingChannels(false);
        }
    };

    const connectToSlack = () => {
        window.location.href = '/slack/auth';
    };

    const disconnectFromSlack = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/slack/disconnect', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                },
            });

            const data = await response.json();
            
            if (response.ok) {
                setConnectionStatus({ connected: false });
                setChannels([]);
                toast({
                    title: "Success",
                    description: "Disconnected from Slack successfully",
                });
            } else {
                throw new Error(data.error || 'Failed to disconnect');
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to disconnect from Slack: " + error.message,
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const sendTestMessage = async () => {
        if (!testChannel) {
            toast({
                title: "Error",
                description: "Please select a channel first",
                variant: "destructive",
            });
            return;
        }

        setIsSendingTest(true);
        try {
            const response = await fetch('/slack/test', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                },
                body: JSON.stringify({
                    channel: testChannel,
                    message: testMessage.trim() || undefined,
                }),
            });

            const data = await response.json();
            
            if (response.ok) {
                toast({
                    title: "Success",
                    description: data.message || "Test message sent successfully!",
                });
                setShowTestModal(false);
                setTestMessage("");
                setTestChannel("");
            } else {
                throw new Error(data.error || 'Failed to send message');
            }
        } catch (error) {
            toast({
                title: "Error",
                description: error.message,
                variant: "destructive",
            });
        } finally {
            setIsSendingTest(false);
        }
    };

    if (isLoading && !connectionStatus) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5" />
                        Your Slack Connection
                    </CardTitle>
                    <CardDescription>
                        Connect your personal Slack account to access your channels
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-center py-8">
                        <RefreshCw className="h-6 w-6 animate-spin" />
                        <span className="ml-2">Loading connection status...</span>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5" />
                        Your Slack Connection
                        {connectionStatus?.connected && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Connected
                            </Badge>
                        )}
                    </CardTitle>
                    <CardDescription>
                        Connect your personal Slack account to send messages to channels you have access to
                    </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-6">
                    {/* Connection Status */}
                    {!connectionStatus?.connected ? (
                        <div className="space-y-4">
                            <Alert>
                                <Info className="h-4 w-4" />
                                <AlertDescription>
                                    Connect your Slack account to send messages to channels you're a member of. 
                                    This uses your personal Slack permissions, so you'll only see channels you can access.
                                </AlertDescription>
                            </Alert>
                            
                            <div className="flex flex-col sm:flex-row gap-3">
                                <Button onClick={connectToSlack} disabled={isLoading}>
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    Connect to Slack
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Connected User Info */}
                            {connectionStatus.user_info && (
                                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                    <div className="flex items-start justify-between">
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <User className="h-4 w-4 text-green-600" />
                                                <span className="font-medium text-green-800">
                                                    Connected as: {connectionStatus.user_info.id}
                                                </span>
                                            </div>
                                            {connectionStatus.team_info && (
                                                <div className="flex items-center gap-2 text-sm">
                                                    <Users className="h-4 w-4 text-green-600" />
                                                    <span className="text-green-700">
                                                        Team: {connectionStatus.team_info.name}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="text-sm text-green-600">
                                                Access to {connectionStatus.channels_count || 0} channels
                                            </div>
                                        </div>
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={disconnectFromSlack}
                                            disabled={isLoading}
                                        >
                                            <Unlink className="h-4 w-4 mr-2" />
                                            Disconnect
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Channels List */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-medium">Your Accessible Channels</h4>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={loadChannels}
                                        disabled={isLoadingChannels}
                                    >
                                        <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingChannels ? 'animate-spin' : ''}`} />
                                        Refresh
                                    </Button>
                                </div>

                                {isLoadingChannels ? (
                                    <div className="flex items-center justify-center py-6">
                                        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                                        Loading channels...
                                    </div>
                                ) : channels.length > 0 ? (
                                    <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {channels.map((channel) => (
                                            <div 
                                                key={channel.id}
                                                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="flex items-center gap-2">
                                                        {channel.is_private ? (
                                                            <Shield className="h-4 w-4 text-gray-500" />
                                                        ) : (
                                                            <Hash className="h-4 w-4 text-gray-500" />
                                                        )}
                                                        <span className="font-medium">{channel.name}</span>
                                                    </div>
                                                    <Badge 
                                                        variant={channel.is_member ? "outline" : "secondary"}
                                                        className={channel.is_member ? "bg-green-50 text-green-700 border-green-200" : ""}
                                                    >
                                                        {channel.is_member ? "Member" : "Not Member"}
                                                    </Badge>
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {channel.num_members} members
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <Alert>
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>
                                            No channels found. Make sure you're a member of some channels in your Slack workspace.
                                        </AlertDescription>
                                    </Alert>
                                )}
                            </div>

                            {/* Test Message */}
                            {channels.some(ch => ch.is_member) && (
                                <div className="pt-4 border-t">
                                    <Button onClick={() => setShowTestModal(true)}>
                                        <Send className="h-4 w-4 mr-2" />
                                        Send Test Message
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Test Message Modal */}
            <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Send Test Message</DialogTitle>
                        <DialogDescription>
                            Send a test message to one of your accessible channels
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="test-channel">Channel</Label>
                            <Select value={testChannel} onValueChange={setTestChannel}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a channel" />
                                </SelectTrigger>
                                <SelectContent>
                                    {channels
                                        .filter(ch => ch.is_member)
                                        .map((channel) => (
                                            <SelectItem key={channel.id} value={channel.name}>
                                                <div className="flex items-center gap-2">
                                                    {channel.is_private ? (
                                                        <Shield className="h-4 w-4" />
                                                    ) : (
                                                        <Hash className="h-4 w-4" />
                                                    )}
                                                    {channel.name}
                                                </div>
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div>
                            <Label htmlFor="test-message">Message (optional)</Label>
                            <Input
                                id="test-message"
                                value={testMessage}
                                onChange={(e) => setTestMessage(e.target.value)}
                                placeholder="Leave blank for default test message"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowTestModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={sendTestMessage} disabled={isSendingTest || !testChannel}>
                            {isSendingTest && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                            Send Test Message
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
} 